import {
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import {
  GetReceiverForecastDetailsQuery,
  useUpdateForecastingAutomationConfigMutation,
  useTriggerForecastingManualRunMutation,
  useForecastingExecutionLogsQuery,
} from "../../../graphql/generated";
import InfoBlock from "../../common/InfoBlock";
import { formatDateTime } from "../../../utils/labels/formatDateTime";
import { PlayArrow, History, Close, Save } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useRoles } from "../../account/Access/RoleGuard";
import ExecutionHistoryView from "../ExecutionHistoryView/ExecutionHistoryView";
import { useConfirm } from "material-ui-confirm";
import { testIds } from "./AutomationConfigPanel.testIds";

export type ReceiverForecastData =
  GetReceiverForecastDetailsQuery["receiverForecastById"];

interface AutomationConfigPanelProps {
  receiverForecast: ReceiverForecastData;
  onUpdate?: () => void;
}

function AutomationConfigPanel({
  receiverForecast,
  onUpdate,
}: AutomationConfigPanelProps) {
  const roles = useRoles();
  
  // Check if user has dispatcher role
  const hasDispatcherAccess = roles.some((role) =>
    ["Carrier Admin", "Manager", "Dispatcher"].includes(role)
  );
  
  // Modal state for execution history
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  
  // Local state for form fields (unsaved changes)
  const [enabled, setEnabled] = useState(
    receiverForecast.automationConfig?.enabled || false
  );
  const [frequencyMinutes, setFrequencyMinutes] = useState<number | "">(
    receiverForecast.automationConfig?.frequencyMinutes || ""
  );
  const [forecastQuantity, setForecastQuantity] = useState<number | "">(
    receiverForecast.automationConfig?.forecastQuantity || ""
  );

  // Validation errors
  const [frequencyError, setFrequencyError] = useState<string>("");
  const [quantityError, setQuantityError] = useState<string>("");
  const [enableError, setEnableError] = useState<string>("");

  // Mutations
  const updateConfigMutation = useUpdateForecastingAutomationConfigMutation();
  const triggerManualRunMutation = useTriggerForecastingManualRunMutation();
  const confirm = useConfirm();

  // Query for last execution (get most recent with limit 1)
  const lastExecutionQuery = useForecastingExecutionLogsQuery(
    {
      receiverForecastId: receiverForecast._id,
      take: 1,
    },
    {
      refetchInterval: receiverForecast.automationConfig?.enabled ? 30000 : false, // Refetch every 30s if enabled
      refetchOnWindowFocus: true,
    }
  );

  // Sync local state with props when they change (reset unsaved changes)
  useEffect(() => {
    setEnabled(receiverForecast.automationConfig?.enabled || false);
    setFrequencyMinutes(
      receiverForecast.automationConfig?.frequencyMinutes || ""
    );
    setForecastQuantity(
      receiverForecast.automationConfig?.forecastQuantity || ""
    );
    // Clear errors when syncing
    setFrequencyError("");
    setQuantityError("");
    setEnableError("");
  }, [receiverForecast.automationConfig]);

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    const savedConfig = receiverForecast.automationConfig;
    return (
      enabled !== (savedConfig?.enabled || false) ||
      frequencyMinutes !== (savedConfig?.frequencyMinutes || "") ||
      forecastQuantity !== (savedConfig?.forecastQuantity || "")
    );
  }, [enabled, frequencyMinutes, forecastQuantity, receiverForecast.automationConfig]);

  // Validate frequency
  const validateFrequency = (value: number | ""): boolean => {
    if (value === "") {
      setFrequencyError("");
      return true;
    }
    if (!Number.isInteger(value) || value < 15) {
      setFrequencyError("Frequency must be at least 15 minutes");
      return false;
    }
    setFrequencyError("");
    return true;
  };

  // Validate quantity
  const validateQuantity = (value: number | ""): boolean => {
    if (value === "") {
      setQuantityError("");
      return true;
    }
    if (!Number.isInteger(value) || value < 1 || value > 1000) {
      setQuantityError("Quantity must be between 1 and 1000");
      return false;
    }
    setQuantityError("");
    return true;
  };

  // Validate enable requirements
  const validateEnableRequirements = (): boolean => {
    if (frequencyMinutes === "" || forecastQuantity === "") {
      setEnableError(
        "Both frequency and quantity must be set before enabling automation"
      );
      return false;
    }
    setEnableError("");
    return true;
  };

  // Check if save button should be disabled
  const isSaveDisabled = useMemo(() => {
    // Disable if no changes
    if (!hasUnsavedChanges) return true;
    
    // Disable if there are validation errors
    if (frequencyError || quantityError) return true;
    
    // Disable if enabling without required fields
    if (enabled && (frequencyMinutes === "" || forecastQuantity === "")) {
      return true;
    }
    
    // Disable if loading
    if (updateConfigMutation.isLoading) return true;
    
    return false;
  }, [
    hasUnsavedChanges,
    frequencyError,
    quantityError,
    enabled,
    frequencyMinutes,
    forecastQuantity,
    updateConfigMutation.isLoading,
  ]);

  // Handle toggle change (local state only)
  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newEnabled = event.target.checked;
    setEnabled(newEnabled);
    
    // Validate requirements if enabling
    if (newEnabled) {
      if (frequencyMinutes === "" || forecastQuantity === "") {
        setEnableError(
          "Both frequency and quantity must be set before enabling automation"
        );
      } else {
        setEnableError("");
      }
    } else {
      setEnableError("");
    }
  };

  // Handle frequency change (local state only)
  const handleFrequencyChange = (value: number | "") => {
    setFrequencyMinutes(value);
    validateFrequency(value);
    
    // Re-validate enable requirements if automation is enabled
    if (enabled && (value === "" || forecastQuantity === "")) {
      setEnableError(
        "Both frequency and quantity must be set before enabling automation"
      );
    } else if (enabled) {
      setEnableError("");
    }
  };

  // Handle quantity change (local state only)
  const handleQuantityChange = (value: number | "") => {
    setForecastQuantity(value);
    validateQuantity(value);
    
    // Re-validate enable requirements if automation is enabled
    if (enabled && (frequencyMinutes === "" || value === "")) {
      setEnableError(
        "Both frequency and quantity must be set before enabling automation"
      );
    } else if (enabled) {
      setEnableError("");
    }
  };

  // Handle save button click
  const handleSave = async () => {
    // Final validation before save
    if (enabled && !validateEnableRequirements()) {
      return;
    }

    if (frequencyMinutes !== "" && !validateFrequency(frequencyMinutes)) {
      return;
    }

    if (forecastQuantity !== "" && !validateQuantity(forecastQuantity)) {
      return;
    }

    try {
      await updateConfigMutation.mutateAsync({
        receiverForecastId: receiverForecast._id,
        config: {
          enabled,
          frequencyMinutes: frequencyMinutes as number,
          forecastQuantity: forecastQuantity as number,
        },
      });
      onUpdate?.();
    } catch (error) {
      console.error("Failed to update automation config:", error);
    }
  };

  // Handle manual trigger
  const handleManualTrigger = async () => {
    try {
      await confirm({
        title: "Trigger Manual Forecasting Run",
        description:
          "This will immediately execute a forecasting run for this receiver. Any unlocked forecasted orders will be regenerated. Are you sure you want to continue?",
        confirmationText: "Run Forecast",
        cancellationText: "Cancel",
      });

      await triggerManualRunMutation.mutateAsync({
        receiverForecastId: receiverForecast._id,
      });
      // Refetch last execution after triggering
      lastExecutionQuery.refetch();
    } catch (error) {
      // User cancelled or error occurred
      if (error !== undefined) {
        console.error("Failed to trigger manual run:", error);
      }
    }
  };

  const lastExecution = lastExecutionQuery.data?.forecastingExecutionLogs?.data?.[0];

  // Determine alert severity based on last execution status
  const getStatusAlertSeverity = (): "success" | "error" | "info" | "warning" => {
    if (!lastExecution) return "info";
    
    switch (lastExecution.status) {
      case "SUCCESS":
        return "success";
      case "FAILED":
        return "error";
      case "IN_PROGRESS":
        return "info";
      default:
        return "info";
    }
  };

  // If user doesn't have dispatcher access, show read-only view
  if (!hasDispatcherAccess) {
    return (
      <InfoBlock title="Automation Configuration">
        <Alert severity="info" sx={{ mb: 2 }}>
          You need Dispatcher, Manager, or Carrier Admin role to configure automation.
        </Alert>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Status: {enabled ? "Enabled" : "Disabled"}
          </Typography>
          {enabled && (
            <>
              {frequencyMinutes && (
                <Typography variant="body2" color="text.secondary">
                  Frequency: {frequencyMinutes} minutes
                </Typography>
              )}
              {forecastQuantity && (
                <Typography variant="body2" color="text.secondary">
                  Forecast Quantity: {forecastQuantity} orders
                </Typography>
              )}
              {receiverForecast.automationConfig?.lastRunAt && (
                <Typography variant="body2" color="text.secondary">
                  Last Run:{" "}
                  {formatDateTime(receiverForecast.automationConfig.lastRunAt)}
                </Typography>
              )}
              {receiverForecast.automationConfig?.nextRunAt && (
                <Typography variant="body2" color="text.secondary">
                  Next Scheduled Run:{" "}
                  {formatDateTime(receiverForecast.automationConfig.nextRunAt)}
                </Typography>
              )}
            </>
          )}
        </Stack>
      </InfoBlock>
    );
  }

  return (
    <InfoBlock title="Automation Configuration">
      <Stack spacing={3}>
        {/* Enable/Disable Toggle */}
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={handleToggleChange}
              inputProps={{ "data-testid": testIds.automationConfigPanelFrequencyMinutesToggle } as React.InputHTMLAttributes<HTMLInputElement>}
              />
            }
            label={
              <Typography variant="body1">
                {enabled ? "Automation Enabled" : "Automation Disabled"}
              </Typography>
            }
          />
          {enableError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {enableError}
            </Alert>
          )}
        </Box>

        {/* Frequency Input */}
        <TextField
          label="Frequency (minutes)"
          type="number"
          value={frequencyMinutes}
          onChange={(e) => {
            const value = e.target.value === "" ? "" : Number(e.target.value);
            handleFrequencyChange(value);
          }}
          onBlur={(e) => {
            const value = e.target.value === "" ? "" : Number(e.target.value);
            validateFrequency(value);
          }}
          error={!!frequencyError}
          helperText={
            frequencyError || "Minimum 15 minutes between forecasting runs"
          }
          fullWidth
          inputProps={{ "data-testid": testIds.automationConfigPanelFrequencyMinutesField,  min: 15 }}
        />

        {/* Forecast Quantity Input */}
        <TextField
          label="Forecast Quantity"
          type="number"
          value={forecastQuantity}
          onChange={(e) => {
            const value = e.target.value === "" ? "" : Number(e.target.value);
            handleQuantityChange(value);
          }}
          onBlur={(e) => {
            const value = e.target.value === "" ? "" : Number(e.target.value);
            validateQuantity(value);
          }}
          error={!!quantityError}
          helperText={
            quantityError || "Number of orders to forecast (1-1000)"
          }
          fullWidth
          inputProps={{ "data-testid": testIds.automationConfigPanelForecastQuantity,  min: 1, max: 1000 }}
        />

        {/* Unsaved Changes Indicator */}
        {hasUnsavedChanges && (
          <Alert severity="warning" sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2">
                You have unsaved changes
              </Typography>
              <Chip label="Unsaved" size="small" color="warning" />
            </Box>
          </Alert>
        )}

        {/* Save Button */}
        <Box>
          <LoadingButton data-testid={testIds.automationConfigPanelSaveConfiguration}
            variant="contained"
            color="primary"
            startIcon={<Save />}
            onClick={handleSave}
            loading={updateConfigMutation.isLoading}
            disabled={isSaveDisabled}
          >
            Save Configuration
          </LoadingButton>
          {updateConfigMutation.isError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Failed to update automation configuration
            </Alert>
          )}
          {updateConfigMutation.isSuccess && (
            <Alert severity="success" sx={{ mt: 1 }}>
              Automation configuration saved successfully
            </Alert>
          )}
        </Box>

        {/* Automation Status Information */}
        {receiverForecast.automationConfig?.enabled && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="h6" gutterBottom>
                Automation Status
              </Typography>
              <Alert severity={getStatusAlertSeverity()} sx={{ mb: 2 }}>
                <Stack spacing={1}>
                  {receiverForecast.automationConfig?.lastRunAt && (
                    <Typography variant="body2">
                      <strong>Last Run:</strong>{" "}
                      {formatDateTime(receiverForecast.automationConfig.lastRunAt)}
                    </Typography>
                  )}
                  {receiverForecast.automationConfig?.nextRunAt && (
                    <Typography variant="body2">
                      <strong>Next Scheduled Run:</strong>{" "}
                      {formatDateTime(receiverForecast.automationConfig.nextRunAt)}
                    </Typography>
                  )}
                  {lastExecution && (
                    <>
                      <Typography variant="body2">
                        <strong>Status:</strong>{" "}
                        {lastExecution.status === "SUCCESS" && "Completed Successfully"}
                        {lastExecution.status === "FAILED" && "Failed"}
                        {lastExecution.status === "IN_PROGRESS" && "In Progress"}
                      </Typography>
                      {lastExecution.ordersGenerated !== null &&
                        lastExecution.ordersGenerated !== undefined && (
                          <Typography variant="body2">
                            <strong>Orders Generated:</strong>{" "}
                            {lastExecution.ordersGenerated}
                          </Typography>
                        )}
                      {lastExecution.status === "FAILED" &&
                        lastExecution.errorMessage && (
                          <Typography variant="body2" color="error">
                            <strong>Error:</strong> {lastExecution.errorMessage}
                          </Typography>
                        )}
                    </>
                  )}
                  {!lastExecution && (
                    <Typography variant="body2" color="text.secondary">
                      No forecasting runs have been executed yet
                    </Typography>
                  )}
                </Stack>
              </Alert>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2}>
                <LoadingButton data-testid={testIds.automationConfigPanelTriggerManualRun}
                  variant="contained"
                  startIcon={<PlayArrow />}
                  onClick={handleManualTrigger}
                  loading={triggerManualRunMutation.isLoading}
                  disabled={!frequencyMinutes || !forecastQuantity}
                >
                  Trigger Manual Run
                </LoadingButton>
                <Button data-testid={testIds.automationConfigPanelViewExecutionHistory}
                  variant="outlined"
                  startIcon={<History />}
                  onClick={() => setHistoryModalOpen(true)}
                >
                  View Execution History
                </Button>
              </Stack>

              {triggerManualRunMutation.isError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Failed to trigger manual run
                </Alert>
              )}
              {triggerManualRunMutation.isSuccess && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Manual run triggered successfully
                </Alert>
              )}
            </Box>
          </>
        )}
      </Stack>

      {/* Execution History Modal */}
      <Dialog
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Execution History</Typography>
            <IconButton data-testid={testIds.automationConfigPanelClose}
              edge="end"
              color="inherit"
              onClick={() => setHistoryModalOpen(false)}
              aria-label="close"
            >
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <ExecutionHistoryView receiverForecastId={receiverForecast._id} />
        </DialogContent>
      </Dialog>
    </InfoBlock>
  );
}

export default AutomationConfigPanel;
